import React, { useState } from 'react';
import { getLabel } from '../data/labels';
import AutoLoader from '../common/AutoLoader';
import ButtonsScroller from '../common/ButtonsScroller';
import FavoriteButton from '../common/FavoriteButton';
import InfoMessage from '../common/InfoMessage';
import LiveBadge from '../common/LiveBadge';
import Loader from '../common/Loader';
import SocialButtonsDropdown from '../common/SocialButtonsDropdown';
import SportsMenuWrapper from '../common/SportsMenuWrapper';
import StickyHeader from '../common/StickyHeader';
import TopMenu from '../common/TopMenu';
import { useShowOnlyFirstBet, useUserSettings } from '../common/UserSettingsProvider';
import { filtraParametriche } from '../data/utils/filtraParametriche';
import { getBetDescriptionBetSelector } from '../data/utils/getBetDescriptionBetSelector';
import { getConfiguration } from '../data/utils/configuration';
import TournamentEvent from '../prematch/TournamentEvent';
import TournamentEventBetsUnder from '../prematch/TournamentEventBetsUnder';
import CategoriesNavbar from './CategoriesNavbar';
import { usePoll } from '../data/fetch/useFetch';
import { parseLiveData } from '../data/parseLiveData';

function groupEventsByTimeAndTournament(categories, sortType) {
    // Get events from all categories
    const tournamentById = new Map();
    const events = [];
    categories.forEach(c => {
        c.tournaments.forEach(t => {
            if (!tournamentById.has(t.uniqueId)) {
                tournamentById.set(t.uniqueId, t);
            }
            t.events.forEach(e =>
                events.push(e)
            );
        })
    });

    // Sort events by time
    events.sort((a, b) => a.timestampDB.localeCompare(b.timestampDB));
    if (sortType === 'desc') {
        events.reverse();
    }

    // Group events by time and tournament
    const tournaments = [];
    const tournamentsSet = new Set();
    events.forEach(e => {
        const tournamentId = `${e.sportRef}_${e.categoryRef}_${e.tournamentRef}`
        if (!tournamentsSet.has(tournamentId)) {
            tournamentsSet.add(tournamentId);
            tournaments.push([]);
        }
        tournaments[tournaments.length - 1].push(e);
    });

    return [tournaments, tournamentById];
}

export function TournamentToggle(props) {
    const userSettings = useUserSettings();
    const conf = getConfiguration();

    const data = props.data;
    const [toggled, setToggled] = useState(true);

    function handleClick(e) {
        e.stopPropagation();
        setToggled(!toggled);
    }

    return (
        <div className="xs_tournamentHeader-wrapper">
            <div className="xs_tournamentHeader z-20 sticky top-0 h-[70px] grid grid-cols-11 items-center bg-cs15 border-b border-b-cs1" onClick={handleClick}>
                <div className="col-start-1 col-end-2 h-full py-1 flex flex-col justify-between items-center">
                    <FavoriteButton id={data.uniqueId} />
                    {userSettings.showSocialButtons &&
                        <SocialButtonsDropdown url={''} />
                    }
                </div>
                <div className="col-start-2 col-end-11 px-1 pb-1 h-full grid grid-flow-row">
                    <div className="flex items-center truncate">
                        <span className="font-bold text-sm text-cs10 truncate">{conf.iwh && `${data.categoryDesc} - `}{data.descrizione}</span>
                        {data.isLive &&
                            <LiveBadge className="inline-flex mr-2" />
                        }
                    </div>
                    <div className="flex items-center truncate icon-tournaments">
                        <div className={"inline-block w-[16px] h-[16px]" + data.categoryIcon}></div>
                        <span className="ml-2 font-bold text-xs text-cs3 uppercase truncate">{data.categoryDesc} ({data.events.length})</span>
                    </div>
                </div>
                <div className="h-full col-start-11 col-end-12 flex items-center justify-center font-bold text-md text-cs10">
                    {toggled ?
                        <i className="bi-chevron-up"></i>
                        :
                        <i className="bi-chevron-down"></i>
                    }
                </div>
            </div>
            {toggled && props.children}
        </div>
    );
}

export function Matches(props) {
    const userSettings = useUserSettings();

    const sortType = userSettings.liveSortingType;

    if (sortType === 'league') {
        const tournaments = [];
        props.categories.map(c =>
            c.tournaments.map(t =>
                tournaments.push(
                    <TournamentToggle key={t.uniqueId} data={t}>
                        {userSettings.betsUnderEvent ?
                            <div className="flex flex-col w-full">
                                {t.events.map(e =>
                                    <TournamentEventBetsUnder key={e.id} data={e} betId={props.betId} />
                                )}
                            </div>
                            :
                            <>
                                {t.events.map(e =>
                                    <TournamentEvent key={e.id} data={e} betId={props.betId} />
                                )}
                            </>
                        }
                    </TournamentToggle>
                )
            )
        );

        return (
            <AutoLoader items={tournaments} limit={2} />
        );
    } else {
        const [tournaments, tournamentById] = groupEventsByTimeAndTournament(props.categories, sortType);

        const elements = tournaments.map(events => {
            const e = events[0];
            const tournamentId = `${e.sportRef}_${e.categoryRef}_${e.tournamentRef}`;
            return (
                <TournamentToggle key={e.uniqueId} data={tournamentById.get(tournamentId)}>
                    {userSettings.betsUnderEvent ?
                        <>
                            {events.map(e =>
                                <TournamentEventBetsUnder key={e.uniqueId} data={e} betId={props.betId} />
                            )}
                        </>
                        :
                        <>
                            {events.map(e =>
                                <TournamentEvent key={e.uniqueId} data={e} betId={props.betId} />
                            )}
                        </>
                    }
                </TournamentToggle>
            );
        });

        return (
            <div className="flex flex-col w-full">
                <AutoLoader items={elements} limit={2} />
            </div>
        );
    }
}

function LiveSport(props) {
    const userSettings = useUserSettings();
    const singleBetMode = useShowOnlyFirstBet();
    let [categoryId, setCategoryId] = useState(0);
    const [betId, setBetId] = useState(null);

    const sport = props.sport;

    function changeSport(sportId) {
        props.changeSport(sportId);
        setCategoryId(categoryId);
    }

    function changeCategory(categoryId) {
        setCategoryId(categoryId);
    }

    function changeBet(betId) {
        setBetId(betId);
    }

    let categories = sport.categories;
    if (categoryId !== 0) {
        if (sport.categoryById[categoryId]) {
            categories = [sport.categoryById[categoryId]];
        } else {
            categoryId = 0;
        }
    } else if (sport.categories.length > 0 && sport.categories[0].id !== 0) {
        categoryId = sport.categories?.[0].id;
    }
    const category = sport.categoryById?.[categoryId] ?? null;
    if (category != null) {
        categories = [category];
    }

    let bets = sport.categories?.[0]?.tournaments?.[0]?.events?.[0]?.bets ?? [];
    bets = filtraParametriche(bets);
    const conf = getConfiguration()

    return (
        <>
            <div className="grid auto-rows-auto">
                <SportsMenuWrapper sports={props.sports}
                    selected={sport}
                    showBackButton={false}
                    showEventsCount={true}
                    selectedCategory={category}
                    showCalendar={true}
                    changeSport={changeSport}
                    changeCategory={changeCategory} 
                    forceScrolling={conf.iwh ? true : false}   
                    />
                {userSettings.showLiveCategories && userSettings.scrollingSportSelector &&
                    <CategoriesNavbar categories={sport.categories} selected={categoryId} changeCategory={changeCategory} />
                }
                {singleBetMode &&
                    <ButtonsScroller>
                        {bets.map((b, i) => {
                            const uniqueId = `${b.id}_0`;
                            const selected = (i === 0 && betId == null) || betId === uniqueId || betId === b.id;
                            const desc = getBetDescriptionBetSelector(b, true);
                            return (
                                <span key={uniqueId}
                                    className={"h-[24px] shrink-0 p-2 mr-2 flex items-center last:mr-0 rounded-lg " + (selected ? 'bg-cs4 text-cs1' : 'bg-cs7 text-cs8')}
                                    onClick={() => changeBet(uniqueId)}>
                                    {desc}
                                </span>
                            );
                        })}
                    </ButtonsScroller>
                }
            </div>
            <Matches categories={categories} betId={betId} />
        </>
    );
}

export default function LiveMatchesLanding() {
    const conf = getConfiguration();

    let [sportId, setSportId] = useState(0);
    function changeSport(sportId) {
        setSportId(sportId);
    }

    const [isLoading, isError, data] = usePoll(conf.api.getMenuLiveGenerale, { sportId }, (data) =>
        data ? parseLiveData(data) : null
    );

    if (isError || (isLoading && data == null)) {
        return (
            <>
                <div className="grid auto-rows-auto">
                    <TopMenu title={getLabel('menu.live')} />
                </div>
                <Loader error={isError} />
            </>
        );
    }

    if (data == null) {
        return (
            <>
                <StickyHeader className="grid auto-rows-auto">
                    <TopMenu title={getLabel('menu.live')} />
                </StickyHeader>
                <InfoMessage message={getLabel('general.no_events')} />
            </>
        );
    }

    if (sportId === 0) {
        sportId = data.sport[0].id;
    }

    return <LiveSport key={sportId} changeSport={changeSport} sports={data.sport} sport={data.sportById[sportId]} />;
}
